<template>
  <div v-if="isLoading">
    <v-skeleton-loader
      type="table-heading"
      class="mb-4"
    />

    <v-row
      class="pb-5"
      style="
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(272px, 1fr));
      "
    >
      <v-col
        v-for="loadingIdx of 24"
        :key="`loading${loadingIdx}`"
      >
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>
  </div>

  <div
    v-else
  >
    <v-tabs
      v-if="hallTables.length > 1"
      v-model="currentHallIdx"
      show-arrows
      background-color="transparent"
      class="mb-5 elevation-0"
    >
      <v-tab
        v-for="(hall, hallTabIdx) in hallTables"
        :key="`hall_tab_${hallTabIdx}`"
      >
        <div class="d-flex align-center">
          <span>{{ hall.attributes.name }}</span>
          <div
            v-if="getHallColorCounts(hall.id).value.red > 0 || getHallColorCounts(hall.id).value.yellow > 0"
            class="ml-2 d-flex align-center"
          >
            <v-badge
              v-if="getHallColorCounts(hall.id).value.red > 0"
              :content="getHallColorCounts(hall.id).value.red"
              :color="colorCodes.red"
              inline
            >
              <div style="width: 8px; height: 8px;"></div>
            </v-badge>
            <v-badge
              v-if="getHallColorCounts(hall.id).value.yellow > 0"
              :content="getHallColorCounts(hall.id).value.yellow"
              :color="colorCodes.yellow + '96'"
              inline
            >
              <div style="width: 8px; height: 8px;"></div>
            </v-badge>
          </div>
        </div>
      </v-tab>
    </v-tabs>

    <v-tabs-items
      v-model="currentHallIdx"
      style="background: transparent"
      class="px-2"
    >
      <v-tab-item
        v-for="(hall, hallIdx) in hallTables"
        :key="`hall_${hallIdx}`"
        eager
      >
        <v-row
          class="pb-5"
          style="
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(272px, 1fr));
          "
        >
          <v-col
            v-for="(table, tableIdx) of hall.tables"
            :key="`table_${tableIdx + 1}`"
          >
            <div v-if="table && table.hold === undefined">
              <table-in-use
                :id="table.id"
                :hall-id="hall.id"
                :table-number="tableIdx + 1"
                :table-prefix="hall.attributes.tablePrefix"
                :table-suffix="hall.attributes.tableSuffix"
                :start-at="table.attributes.startAt"
                :customer-count="table.attributes.customerCount"
                :subtotal="table.meta.subtotal"
                :total="table.meta.total"
                :expire-at="table.meta.expireAt"
                :current-nominations="table.meta.currentNominations.data"
                :referring-histories="table.attributes.referringHistories.data"
                :customer-table-taggings="table.attributes.customerTableTaggings.data"
                :customers="table.attributes.customers || []"
                :total-deposit-amount="table.meta.totalDepositAmount || 0"
                :deposits="table.meta.deposits || []"
                :finish-notice-minutes-date="finishNoticeMinutesDate"
                :finish-notice-color="getFinishNoticeColor(hall.id, table.id).value"
                :color-codes="colorCodes"
                @finish-notice-colors="onFinishNoticeColors"
                @clicked="openShowTable({ hallIdx, tableIdx })"
                @move-table="moveTable(hall.id, table.id, $event)"
                @destroy="destroyTable(hall.id, table.id)"
                @updated:table="spliceAndBroadcastTable($event)"
              />

              <!-- table-show-drawer はv-forの外に1つだけ配置 -->
            </div>

            <div v-else>
              <table-not-in-use
                :table="table"
                :table-number="tableIdx+1"
                :table-prefix="hall.attributes.tablePrefix"
                :table-suffix="hall.attributes.tableSuffix"
                @clicked="openNewTable({ hallIdx, tableIdx })"
              />

              <!-- table-create-drawer はv-forの外に1つだけ配置 -->
            </div>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <table-show-drawer
      v-if="selectedTableData && selectedTableData.table"
      :key="`table-show-${selectedTableData.table.id}`"
      :is-visible="targetTableActions.show"
      :table-id="selectedTableData.table.id"
      :table-number="selectedTableData.tableIdx + 1"
      :table-prefix="selectedTableData.hall.attributes.tablePrefix"
      :table-suffix="selectedTableData.hall.attributes.tableSuffix"
      :sequence-number="selectedTableData.table.attributes.sequenceNumber"
      :start-at="selectedTableData.table.attributes.startAt"
      :today="today"
      :customer-count="selectedTableData.table.attributes.customerCount"
      :subtotal="selectedTableData.table.meta.subtotal"
      :total="selectedTableData.table.meta.total"
      :paid-in-advance-amount="selectedTableData.table.meta.paidInAdvanceAmount"
      :expire-at="selectedTableData.table.meta.expireAt"
      :current-nominations="selectedTableData.table.meta.currentNominations.data"
      :course-overview="selectedTableData.table.meta.courseOverview.data"
      :customer-table-taggings="selectedTableData.table.attributes.customerTableTaggings.data"
      :default-tab-idx="defaultTableShowTabIdx"
      :courses="selectedTableData.hall.attributes.tableInitConfig.data.attributes.courses.data"
      :self-order-status="selectedTableData.table.attributes.selfOrderStatus"
      :hall-id="selectedTableData.hall.id"
      :hall-name="selectedTableData.hall.attributes.name"
      :initial-deposits="selectedTableData.table.meta.deposits || []"
      :total-deposit-amount="selectedTableData.table.meta.totalDepositAmount || 0"
      @updated="updateTable($event)"
      @checked="checkoutTable(selectedTableData.hallIdx, $event)"
      @close="closeDrawer({ strategy: null })"
    />

    <!-- feature flag OFF: 既存の右オーバーレイドロワー -->
    <table-create-drawer
      v-if="selectedTableData && !selectedTableData.table && !useTableCreateWizard"
      :is-visible="targetTableActions.create"
      :hall-id="selectedTableData.hall.id"
      :table-number="selectedTableData.tableIdx + 1"
      :table-prefix="selectedTableData.hall.attributes.tablePrefix"
      :table-suffix="selectedTableData.hall.attributes.tableSuffix"
      :referrals="selectedTableData.hall.attributes.tableInitConfig.data.attributes.referrals.data"
      :nominations="selectedTableData.hall.attributes.tableInitConfig.data.attributes.nominations.data"
      :courses="selectedTableData.hall.attributes.tableInitConfig.data.attributes.courses.data"
      :items="selectedTableData.hall.attributes.tableInitConfig.data.attributes.items.data"
      @close="closeDrawer({ strategy: actionType.delete })"
      @created="addTable($event)"
    />

    <!-- feature flag ON: ステップ方式ウィザード -->
    <table-create-wizard
      v-if="selectedTableData && !selectedTableData.table && useTableCreateWizard"
      :value="targetTableActions.create"
      :hall-id="selectedTableData.hall.id"
      :table-number="selectedTableData.tableIdx + 1"
      :table-prefix="selectedTableData.hall.attributes.tablePrefix"
      :table-suffix="selectedTableData.hall.attributes.tableSuffix"
      :referrals="normalizeForWizard(selectedTableData.hall.attributes.tableInitConfig.data.attributes.referrals.data)"
      :nominations="normalizeForWizard(selectedTableData.hall.attributes.tableInitConfig.data.attributes.nominations.data)"
      :courses="normalizeForWizard(selectedTableData.hall.attributes.tableInitConfig.data.attributes.courses.data)"
      :items="normalizeForWizard(selectedTableData.hall.attributes.tableInitConfig.data.attributes.items.data)"
      :users="normalizeForWizard(users)"
      :checkout-config="selectedHallCheckoutConfig"
      :vending-contribution-triggers="vendingContributionTriggers"
      @close="closeDrawer({ strategy: actionType.delete })"
      @created="addTable($event)"
    />

    <QrCode
      v-if="receipt.id && receipt.attributes.receiptImages.length > 0"
      :key="`receipt-${receipt.id}`"
      :urls="receipt.attributes.receiptImages"
      :immediate-share="true"
      class="mt-1"
      @closed="receipt = {}"
    />
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  provide,
  inject,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'
import {
  map,
  find,
  get,
  uniqBy,
  flatMap,
  findIndex,
  sumBy,
  cloneDeep,
  groupBy,
  orderBy,
} from 'lodash'
import { useQrCodeSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import { format, parseISO } from 'date-fns'
import HallApi from '@/api/waiter/Hall'
import UserApi from '@/api/waiter/User'
import VendibleApi from '@/api/waiter/Vendible'
import ReferralApi from '@/api/waiter/Referral'
import VendingContributionTriggerApi from '@/api/waiter/VendingContributionTrigger'
import TimecardApi from '@/api/waiter/Timecard'
import TableApi from '@/api/waiter/Table'
import CustomerApi from '@/api/v2/admin/customer-management/Customer'
import TableInUse from '@/views/components/table/TableInUse.vue'
import TableNotInUse from '@/views/components/table/TableNotInUse.vue'
import TableCreateDrawer from '@/views/components/table/TableCreateDrawer.vue'
import TableCreateWizard from '@/views/components/table/TableCreateWizard/TableCreateWizard.vue'
import useFeatureFlags from '@/views/composable/useFeatureFlags'
import TableShowDrawer from '@/views/components/table/TableShowDrawer/TableShowDrawer.vue'
import useQueryString from '@/views/composable/useQueryString'
import useCurrentData from '@/views/composable/useCurrentData'
import useCompInit from '@/views/composable/useCompInit'
import { useDateRange } from '@/views/v2-temp/common/composables/useDateRange'
import { cable } from '@/plugins/websocket-client'
import QrCode from '@/views/components/printer/QrCode.vue'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'

export default {
  components: {
    TableInUse,
    TableNotInUse,
    TableCreateDrawer,
    TableCreateWizard,
    TableShowDrawer,
    QrCode,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { flags: featureFlags } = useFeatureFlags()
    const useTableCreateWizard = computed(() => featureFlags.value.useTableCreateWizard)

    // 既存の data:[{attributes:{...}}] 形式を ウィザード用のフラットなオブジェクトに変換
    const normalizeForWizard = list => (list || []).map(record => ({
      id: record.id,
      ...record.attributes,
    }))

    const { currentUser, currentClub } = useCurrentData()
    const { id: currentUserId } = currentUser.value
    const currentHallIdx = ref(0)
    const targetTable = reactive({ hallIdx: undefined, tableIdx: undefined })
    const targetTableActions = reactive({
      create: false, show: false, created: false, updated: false,
    })
    const halls = ref([])
    const actionType = {
      new: 'new',
      create: 'create',
      delete: 'delete',
      confirm: 'confirm',
    }

    const colorCodes = {
      red: '#D74A4A',
      yellow: '#F4C645',
    }

    const tableFinishNoticeColors = ref({})

    const { isLoading, initWith } = useCompInit()
    const currentClubMeta = inject('currentClubMeta', {})
    const getClub = inject('getClub', () => {})
    const fetchPlusMinusStats = inject('fetchPlusMinusStats', () => {})
    const fetchOperationRateStats = inject('fetchOperationRateStats', () => {})
    const noticeSubscription = inject('noticeSubscription', () => {})
    const { today } = useDateRange()

    // NOTE: Hallの子供Comp全体で使える普遍的なやつはHallからprovideする
    const users = ref([])
    const vendibles = ref([])
    const courses = ref([])
    const nominations = ref([])
    const items = ref([])
    const referrals = ref([])
    const vendingContributionTriggers = ref([])
    const timecards = ref([])
    const receipt = ref({})

    provide('users', users)
    provide('vendibles', vendibles)
    provide('courses', courses)
    provide('nominations', nominations)
    provide('items', items)
    provide('referrals', referrals)
    provide('vendingContributionTriggers', vendingContributionTriggers)
    provide('timecards', timecards)
    provide('halls', halls)

    const { route, resetQuery } = useQueryString()

    const otherWaiterUsesTable = (hallIdx, tableNumber, fromUserId) => {
      if (fromUserId !== currentUserId) {
        const targetHallNumber = hallIdx + 1
        const targetTableNumber = tableNumber + 1

        halls.value[hallIdx].attributes.tables.data.push(
          {
            attributes: {
              hallId: targetHallNumber,
              tableNumber: targetTableNumber,
            },
            id: String(targetTableNumber),
            hold: true,
            meta: {},
          },
        )
      }
    }

    const releaseOtherWaiterUsesTable = (hallIdx, tableNumber, fromUserId) => {
      if (fromUserId !== currentUserId) {
        const targetTableNumber = tableNumber

        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return

        const tableIdx = findIndex(
          targetHall.attributes.tables.data,
          table => Number(table.attributes.tableNumber) === Number(targetTableNumber),
        )
        targetHall.attributes.tables.data.splice(tableIdx, 1)
      }
    }
    const updateOtherWaiterTable = ({
      hallIdx, fromUserId, tableData,
    }) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return

        const tableIdx = findIndex(
          targetHall.attributes.tables.data,
          table => Number(table.attributes.tableNumber) === Number(tableData.attributes.tableNumber),
        )
        targetHall.attributes.tables.data.splice(tableIdx, 1, tableData)
      }
    }

    const addOtherWaiterTable = ({ hallIdx, tableData, fromUserId }) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return

        targetHall.attributes.tables.data.push(tableData)
      }
    }

    const removeOtherWaiterTable = ({ hallIdx, tableId, fromUserId }) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return

        const tableIdx = findIndex(
          targetHall.attributes.tables.data,
          table => Number(table.id) === Number(tableId),
        )
        if (tableIdx >= 0) {
          targetHall.attributes.tables.data.splice(tableIdx, 1)
        }
      }
    }

    const reservedOtherWaiterTable = fromUserId => {
      if (fromUserId !== currentUserId && targetTableActions.create) {
        const { hallIdx, tableIdx } = targetTable
        // eslint-disable-next-line no-use-before-define
        subscription.occupyPlace(hallIdx, tableIdx, 'new')
      }
    }

    const syncTables = hallIdx => {
      // eslint-disable-next-line no-use-before-define
      subscription.confirmReservedOccupyPlace(hallIdx)
    }

    // websocketストアにHallChannel登録
    vm.$store.commit('websocket/REGISTER_CHANNEL', 'hall')

    const subscription = cable.subscriptions.create(
      {
        channel: 'HallChannel',
      },
      {
        connected: () => {
          vm.$store.dispatch('websocket/channelConnected', 'hall')
        },
        disconnected: () => {
          vm.$store.dispatch('websocket/channelDisconnected', 'hall')
        },
        received: ({
          hallIdx,
          tableNumber,
          tableId,
          fromUserId,
          strategy,
          tableData,
        }) => {
          // NOTE: 自分が以外のユーザが操作したときの通知で主に使われます
          switch (strategy) {
            case 'new':
              otherWaiterUsesTable(hallIdx, tableNumber, fromUserId)
              break
            case 'updateTable':
              updateOtherWaiterTable({
                hallIdx, tableData, fromUserId,
              })
              break
            case 'addTable':
              addOtherWaiterTable({ hallIdx, tableData, fromUserId })
              break
            case 'removeTable':
              removeOtherWaiterTable({ hallIdx, tableId, fromUserId })
              break
            case 'delete':
              releaseOtherWaiterUsesTable(hallIdx, tableNumber, fromUserId)
              break
            case 'confirm':
              reservedOtherWaiterTable(fromUserId)
              break
            default:
              break
          }
        },
        occupyPlace: (hallIdx, tableNumber, strategy) => {
          // NOTE: 対象のhallの該当のtableを保有する

          return subscription.perform('occupy_place', {
            hallIdx,
            tableNumber,
            currentUserId,
            strategy,
          })
        },
        deleteOccupyPlace: ({ hallIdx, tableNumber }) => {
          return subscription.perform('delete_occupy_place', {
            hallIdx,
            tableNumber,
            currentUserId,
          })
        },
        confirmReservedOccupyPlace: hallIdx => {
          return subscription.perform('confirm_reserved_occupy_place', {
            hallIdx,
            currentUserId,
          })
        },
        updateTable: ({ hallIdx, tableData }) => {
          return subscription.perform('update_table', {
            hallIdx,
            currentUserId,
            tableData,
          })
        },
        addTable: ({ hallIdx, tableData }) => {
          return subscription.perform('add_table', {
            hallIdx,
            currentUserId,
            tableData,
          })
        },
        removeTable: ({ hallIdx, tableId }) => {
          return subscription.perform('remove_table', {
            hallIdx,
            tableId,
            currentUserId,
          })
        },
      },
    )

    const vendibleTags = computed(() => {
      return uniqBy(orderBy(flatMap(vendibles.value, 'attributes.tags.data'), 'attributes.position'), 'id')
    })
    provide('vendibleTags', vendibleTags)

    const defaultTableShowTabIdx = computed(() => {
      if (route.value.query.vendingHistoryId || route.value.query.receipt) return 2

      return 0
    })

    const usersWithTimecard = computed(() => {
      // timecards.valueのコピーを作成し、start_atによって降順に並び替える
      const sortedTimecards = [...timecards.value].sort((a, b) => {
        // `start_at` を使用して降順にソート
        return b.attributes.startAt.localeCompare(a.attributes.startAt)
      })

      // props.usersの各ユーザーについて、最も最近のtimecardを見つける
      const usersWithLatestTimecard = users.value
        .map(user => {
          // 各ユーザーに紐づく最新のタイムカードを探す
          const userLatestTimecard = sortedTimecards.find(timecard => +timecard.attributes.userId === +user.id)

          // ユーザーに紐づくタイムカードがあればユーザー情報とマージし、タイムカードがなければnullを返す
          return userLatestTimecard
            ? {
              ...user,
              timecard: userLatestTimecard.attributes, // タイムカードがある場合はその属性を含む
            }
            : null // タイムカードがないユーザーの場合はnullを返す
        })
        .filter(user => user !== null) // nullでないユーザーのみを保持する

      return usersWithLatestTimecard
    })
    provide('usersWithTimecard', usersWithTimecard)

    const getHalls = async () => {
      const res = await HallApi.getHalls({ isOperation: true })

      if (res?.data) {
        halls.value = [...res.data.halls.data]
      }
    }

    // HallChannel再接続時に卓データを再取得
    const hallConnected = computed(() => vm.$store.state.websocket.channels.hall?.connected)
    let prevHallConnected = null
    watch(hallConnected, newVal => {
      if (prevHallConnected === false && newVal === true) {
        getHalls().catch(err => console.error('[WebSocket] Failed to refetch halls:', err))
      }
      prevHallConnected = newVal
    })

    const getUsers = async () => {
      const res = await UserApi.getUsers()

      if (res?.data) {
        users.value = [...res.data.users.data]
      }
    }

    const getVendibles = async () => {
      const res = await VendibleApi.getVendibles()

      if (res?.data) {
        vendibles.value = [...res.data.vendibles.data]

        const vendiblesByType = groupBy(vendibles.value, 'attributes.vendibleType')
        courses.value = vendiblesByType.Course
        nominations.value = vendiblesByType.Nomination
        items.value = vendiblesByType.Item
      }
    }

    const getReferrals = async () => {
      const res = await ReferralApi.getReferrals()

      if (res?.data) {
        referrals.value = [...res.data.referrals.data]
      }
    }

    const getVendingContributionTriggers = async () => {
      const res = await VendingContributionTriggerApi.getVendingContributionTriggers()

      if (res?.data) {
        vendingContributionTriggers.value = [...res.data.vendingContributionTriggers.data]
      }
    }

    const getTimecards = async () => {
      const res = await TimecardApi.getTimecards({ isToday: true })

      if (res?.data) {
        timecards.value = [...res.data.timecards.data]
      }
    }

    // 顧客検索（検索クエリがある場合のみAPIを呼ぶ）
    const searchCustomers = async (query = '') => {
      if (!query || query.trim().length === 0) {
        return []
      }
      try {
        const res = await CustomerApi.getCustomers({ q: query.trim() })
        if (res?.data?.data) {
          return res.data.data
        }
      } catch (error) {
        console.error('Failed to search customers:', error)
      }

      return []
    }
    provide('searchCustomers', searchCustomers)

    const arrangeTables = hall => {
      // 空席を用意する
      return map(Array(hall.attributes.tableCount).fill(null), (_, idx) => {
        const tables = get(hall, 'attributes.tables.data')

        return find(tables, table => Number(get(table, 'attributes.tableNumber')) === (idx + 1))
      })
    }

    const hallTables = computed(() => {
      return map(halls.value, hall => {
        return {
          ...hall,
          tables: arrangeTables(hall),
        }
      })
    })

    const activeTables = computed(() => {
      return flatMap(halls.value, hall => hall.attributes.tables.data)
    })

    // eslint-disable-next-line no-use-before-define
    const selectedTableData = computed(() => {
      const { hallIdx, tableIdx } = targetTable
      if (hallIdx === undefined || tableIdx === undefined) return null

      const hall = hallTables.value[hallIdx]
      if (!hall) return null

      const table = hall.tables[tableIdx] || null

      return {
        hall, table, hallIdx, tableIdx,
      }
    })

    // 選択中ホールの checkoutConfig（税・サービス料・丸めなど）を平坦化
    const selectedHallCheckoutConfig = computed(() => {
      const hall = selectedTableData.value?.hall
      const attrs = hall?.attributes?.checkoutConfig?.data?.attributes
        || hall?.attributes?.checkoutConfig

      return attrs || {}
    })

    const finishNoticeMinutesDate = computed(() => {
      const first = currentClub.value.firstFinishNoticeMinutes
      const second = currentClub.value.secondFinishNoticeMinutes

      return { first, second }
    })

    const getFinishNoticeColor = (hallId, tableId) => {
      return computed(() => {
        const numericHallId = +hallId
        const numericTableId = +tableId
        // tableFinishNoticeColors全体を監視するため、.valueを使用
        const colors = tableFinishNoticeColors.value

        return (colors[numericHallId] && colors[numericHallId][numericTableId]) || null
      })
    }

    const getHallColorCounts = hallId => {
      return computed(() => {
        const numericHallId = +hallId
        const colors = tableFinishNoticeColors.value[numericHallId] || {}

        let redCount = 0
        let yellowCount = 0

        Object.values(colors).forEach(color => {
          if (color === 'red') redCount += 1
          else if (color === 'yellow') yellowCount += 1
        })

        return { red: redCount, yellow: yellowCount }
      })
    }

    const onFinishNoticeColors = ({ hallId, tableId, color }) => {
      console.log('onFinishNoticeColors', hallId, tableId, color)
      if (hallId === undefined || tableId === undefined || color === undefined) return

      const numericHallId = +hallId
      const numericTableId = +tableId

      // Vueの反応性を保つために、新しいオブジェクトを作成
      if (tableFinishNoticeColors.value[numericHallId]) {
        tableFinishNoticeColors.value = {
          ...tableFinishNoticeColors.value,
          [numericHallId]: {
            ...tableFinishNoticeColors.value[numericHallId],
            [numericTableId]: color,
          },
        }
      } else {
        tableFinishNoticeColors.value = {
          ...tableFinishNoticeColors.value,
          [numericHallId]: {
            [numericTableId]: color,
          },
        }
      }
    }

    // QRコード印刷: 卓作成時の自動印刷用
    const { printer: receiptPrinter, checkConnection: checkReceiptPrinterConnection } = useReceiptPrinterConnection()
    const qrPrintOptions = ref({
      ipAddress: '',
      qrCodeUrl: '',
      hallName: '',
      tableName: '',
      startAt: '',
    })
    const qrPrintOptionsComputed = computed(() => ({
      ...qrPrintOptions.value,
      printer: receiptPrinter.value,
    }))
    const { send: sendQrSlipPrint, onReceive: onReceiveQrPrint } = useQrCodeSlipPrint(qrPrintOptionsComputed)
    onReceiveQrPrint.value = response => {
      if (!response.success) {
        vm.$toast.error('QRコードの印刷に失敗しました')
      }
    }

    const printQrCodeForTable = async (tableData, hallData) => {
      try {
        const tableId = tableData.id
        const res = await TableApi.getSelfOrderQr(tableId)
        const data = res.data.data || res.data

        // セキュアモード（動的QR）の場合のみ自動印刷
        if (data.security_level !== 'secure') return

        const tablePrefix = hallData.attributes.tablePrefix || ''
        const tableSuffix = hallData.attributes.tableSuffix || ''
        const tableName = `${tablePrefix}${tableData.attributes.tableNumber}${tableSuffix}`

        let formattedStartAt = ''
        if (tableData.attributes.startAt) {
          try {
            formattedStartAt = format(parseISO(tableData.attributes.startAt), 'yyyy/MM/dd HH:mm')
          } catch {
            formattedStartAt = tableData.attributes.startAt
          }
        }

        qrPrintOptions.value = {
          ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
          qrCodeUrl: data.url,
          hallName: hallData.attributes.name || '',
          tableName,
          startAt: formattedStartAt,
        }

        await checkReceiptPrinterConnection()
        await sendQrSlipPrint()
        vm.$toast.success('QRコードを印刷しました')
      } catch (error) {
        console.error('QRコード自動印刷エラー:', error)
        vm.$toast.error('QRコードの印刷に失敗しました')
      }
    }

    const addTable = tableData => {
      const targetHall = find(halls.value, hall => Number(hall.id) === Number(get(tableData, 'attributes.hallId')))
      if (!targetHall?.attributes) return

      const { hallIdx } = targetTable
      targetHall.attributes.tables.data.push(tableData)

      // NOTE: 作成完了済みに変更
      targetTableActions.created = true
      subscription.updateTable({ hallIdx, tableData })

      Promise.all([getClub(), fetchPlusMinusStats(), fetchOperationRateStats()])

      // セルフオーダーがopenの場合、QRコードを自動印刷
      if (tableData.attributes.selfOrderStatus === 'open') {
        printQrCodeForTable(tableData, targetHall)
      }
    }

    const spliceAndBroadcastTable = tableData => {
      const hallIdx = halls.value.findIndex(hall => Number(hall.id) === Number(get(tableData, 'attributes.hallId')))
      if (hallIdx === -1) return null

      const targetHall = halls.value[hallIdx]
      const tableIdx = findIndex(
        targetHall.attributes.tables.data,
        table => Number(table.attributes.tableNumber) === Number(tableData.attributes.tableNumber),
      )
      if (tableIdx === -1) return null

      targetHall.attributes.tables.data.splice(tableIdx, 1, tableData)
      subscription.updateTable({ hallIdx, tableData })

      return { hallIdx, tableIdx }
    }

    const updateTable = tableData => {
      if (!spliceAndBroadcastTable(tableData)) return

      Promise.all([getClub(), fetchPlusMinusStats(), fetchOperationRateStats()])
    }

    const openDrawer = (actionKey, { hallIdx, tableIdx, strategy = null }) => {
      // NOTE: テーブルの選択
      targetTable.hallIdx = hallIdx
      targetTable.tableIdx = tableIdx
      // NOTE: 作成 or 詳細確認の一致する方のフラグが変わる
      targetTableActions[actionKey] = true
      // NOTE: tableを占有したことをほかのメンバーに通知
      subscription.occupyPlace(hallIdx, tableIdx, strategy)
    }

    const openNewTable = ({ hallIdx, tableIdx }) => {
      openDrawer('create', { hallIdx, tableIdx, strategy: actionType.new })
    }

    const openShowTable = ({ hallIdx, tableIdx }) => {
      openDrawer('show', { hallIdx, tableIdx })
    }

    const closeDrawer = ({ strategy }) => {
      // NOTE: テーブルの選択の解除
      const { hallIdx, tableIdx } = targetTable
      const { create, created } = targetTableActions
      targetTable.hallIdx = undefined
      targetTable.tableIdx = undefined
      targetTableActions.create = false
      targetTableActions.created = false
      targetTableActions.show = false
      targetTableActions.updated = false
      resetQuery(true)

      const tableNumber = tableIdx + 1

      if (create && !created) subscription.deleteOccupyPlace({ hallIdx, tableNumber })
    }

    const checkoutTable = async (hallIdx, receiptData) => {
      const tableIdx = findIndex(
        halls.value[hallIdx].attributes.tables.data,
        table => +table.id === +receiptData.attributes.tableId,
      )
      // NOTE: 消えてしまうので先にテーブルIDを取得
      const tableId = +halls.value[hallIdx].attributes.tables.data[tableIdx].id
      halls.value[hallIdx].attributes.tables.data.splice(tableIdx, 1)

      vm.$toast.success(`${receiptData.meta.tableName}の会計をしました`)
      subscription.deleteOccupyPlace({ hallIdx, tableNumber: receiptData.meta.tableNumber })

      receipt.value = receiptData

      const hallId = +halls.value[hallIdx].id
      if (tableFinishNoticeColors.value[hallId] && tableFinishNoticeColors.value[hallId][tableId]) {
        tableFinishNoticeColors.value = {
          ...tableFinishNoticeColors.value,
          [hallId]: {
            ...tableFinishNoticeColors.value[hallId],
            [tableId]: null,
          },
        }
      }

      Promise.all([getClub(), fetchPlusMinusStats()], fetchOperationRateStats())
    }

    const moveTable = async (currentHallId, tableId, { hallId, tableNumber, callback }) => {
      try {
        const res = await TableApi.moveTable(tableId, { hallId, tableNumber })
        if (res?.data?.data) {
          const { data: tableData } = res.data
          const updatedHalls = [...halls.value]

          // 既存の場所を探す
          const hallIdx = findIndex(updatedHalls, hall => Number(hall.id) === Number(currentHallId))
          if (hallIdx < 0) return
          const tableIdx = findIndex(
            updatedHalls[hallIdx].attributes.tables.data,
            table => Number(table.id) === Number(tableData.id),
          )
          if (tableIdx < 0) return
          // 移動元のテーブルを削除
          updatedHalls[hallIdx].attributes.tables.data.splice(tableIdx, 1)

          // 移動先を探す
          const { hallId: newHallId } = tableData.attributes
          const newHallIdx = findIndex(updatedHalls, hall => Number(hall.id) === Number(newHallId))
          if (newHallIdx < 0) return
          // 移動先にテーブルを追加
          updatedHalls[newHallIdx].attributes.tables.data.push(tableData)

          // 移動元のホールの特定テーブルの色を削除
          if (tableFinishNoticeColors.value[currentHallId]) {
            delete tableFinishNoticeColors.value[currentHallId][tableId]
          }

          halls.value = updatedHalls

          // 他クライアントに通知: 移動元の削除 + 移動先の追加
          subscription.removeTable({ hallIdx, tableId })
          subscription.addTable({ hallIdx: newHallIdx, tableData })

          vm.$toast.success('卓を移動しました')
        }
      } catch (e) {
        console.error(e)
        vm.$toast.error('卓の移動に失敗しました')
      } finally {
        callback()
      }
    }

    const destroyTable = async (currentHallId, tableId) => {
      try {
        const res = await TableApi.deleteTable(tableId)
        console.log(res)
        if (res?.data) {
          const updatedHalls = [...halls.value]

          // 現在の場所を探す
          const hallIdx = findIndex(updatedHalls, hall => Number(hall.id) === Number(currentHallId))
          if (hallIdx < 0) return
          const tableIdx = findIndex(updatedHalls[hallIdx].attributes.tables.data, table => Number(table.id) === Number(tableId))
          if (tableIdx < 0) return
          // テーブルを削除
          updatedHalls[hallIdx].attributes.tables.data.splice(tableIdx, 1)

          // ホールを更新
          halls.value = updatedHalls
          if (tableFinishNoticeColors.value[currentHallId]) {
            delete tableFinishNoticeColors.value[currentHallId]
          }

          // 他クライアントに卓削除を通知
          subscription.removeTable({ hallIdx, tableId })

          vm.$toast.success('卓を削除しました')
        }
      } catch (e) {
        console.error(e)
        vm.$toast.error('卓の削除に失敗しました')
      }
    }

    // NOTE: 未精算の合計金額を算出する
    watch(() => cloneDeep(activeTables.value), (newVal, _prevVal) => {
      currentClubMeta.todaySalesUnfixed = sumBy(newVal, o => Number(o.meta.total))
    }, { immediate: true })

    // SelfOrderChannelからの卓データ更新通知を監視
    const latestTableUpdate = computed(() => vm.$store.state.selfOrder?.latestTableUpdate)
    const refreshTableById = async tableId => {
      try {
        const res = await TableApi.getTable(tableId)
        if (res?.data?.data) {
          const tableData = res.data.data
          const targetHall = find(halls.value, hall => Number(hall.id) === Number(tableData.attributes.hallId))
          if (!targetHall?.attributes) return

          const tableIdx = findIndex(
            targetHall.attributes.tables.data,
            table => Number(table.id) === Number(tableId),
          )

          if (tableIdx >= 0) {
            targetHall.attributes.tables.data.splice(tableIdx, 1, tableData)
            vm.$toast.info(`${tableData.meta.tableName || '卓'}のデータが更新されました`)
          }
        }
      } catch (error) {
        console.error('Failed to refresh table:', error)
      }
    }

    watch(latestTableUpdate, newUpdate => {
      if (newUpdate?.tableId) {
        refreshTableById(newUpdate.tableId)
      }
    }, { deep: true })

    initWith([
      getHalls(),
      getUsers(),
      getVendibles(),
      getReferrals(),
      getVendingContributionTriggers(),
      getTimecards(),
    ]).then(() => {
      if (route.value.query.tableId) {
        const tableToShow = find(activeTables.value, o => Number(o.id) === Number(route.value.query.tableId))
        targetTable.hallIdx = findIndex(halls.value, o => Number(o.id) === Number(tableToShow.attributes.hallId))
        targetTable.tableIdx = findIndex(hallTables.value[targetTable.hallIdx].tables, o => Number(o?.id) === Number(tableToShow.id))
        targetTableActions.show = true
      }
      // NOTE: hallデータ取得完了後に他のwaiterが操作しているデータを取得しセットする
      syncTables(currentHallIdx)
    })

    return {
      // data
      currentUserId,
      isLoading,
      currentHallIdx,
      targetTable,
      targetTableActions,
      useTableCreateWizard,
      selectedHallCheckoutConfig,
      normalizeForWizard,
      users,
      vendibles,
      referrals,
      vendingContributionTriggers,
      receipt,
      tableFinishNoticeColors,
      colorCodes,
      today,
      // computed
      hallTables,
      activeTables,
      selectedTableData,
      defaultTableShowTabIdx,
      courses,
      nominations,
      items,
      finishNoticeMinutesDate,

      // methods
      openDrawer,
      openNewTable,
      openShowTable,
      closeDrawer,
      addTable,
      updateTable,
      spliceAndBroadcastTable,
      checkoutTable,
      moveTable,
      destroyTable,
      onFinishNoticeColors,

      // websocket
      subscription,
      noticeSubscription,
      actionType,

      usersWithTimecard,

      // computed functions
      getFinishNoticeColor,
      getHallColorCounts,

      icons: {},
    }
  },
}
</script>
